import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import CookieConsent from './components/CookieConsent';
import SupportButton from './components/SupportButton';
import AccessDenied from './components/AccessDenied';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Employees from './pages/Employees';
import Risks from './pages/Risks';
import Assessments from './pages/Assessments';
import PGR from './pages/PGR';
import ActionPlans from './pages/ActionPlans';
import Reports from './pages/Reports';
import Analytics from './pages/Analytics';
import Alerts from './pages/Alerts';
import Trainings from './pages/Trainings';
import Ranking from './pages/Ranking';
import Turnover from './pages/Turnover';
import Offboarding from './pages/Offboarding';
import AbsencesReport from './pages/AbsencesReport';
import PublicAssessment from './pages/PublicAssessment';
import AcceptInvite from './pages/AcceptInvite';
import UserManagement from './pages/UserManagement';
import Subscription from './pages/Subscription';
import { SubscriptionSuccess, SubscriptionError, SubscriptionPending } from './pages/SubscriptionCallback';
import Integrations from './pages/Integrations';
import ESocialConfig from './pages/ESocialConfig';
import AIAnalysis from './pages/AIAnalysis';
import ResetPassword from './pages/ResetPassword';
import Files from './pages/Files';
import CompanyProfile from './pages/CompanyProfile';
import MasterDashboard from './pages/MasterDashboard';
import EmployeePortal from './pages/EmployeePortal';
import AEP from './pages/AEP';
import TermosDeUso from './pages/TermosDeUso';
import PoliticaDePrivacidade from './pages/PoliticaDePrivacidade';
import MeuContrato from './pages/MeuContrato';
import HelpCenter from './pages/HelpCenter';
import AtivarConta from './pages/AtivarConta';
import NotificationLogs from './pages/NotificationLogs';
import DDSManagement from './pages/DDSManagement';
import MeusDDS from './pages/MeusDDS';
import AccountBlocked from './pages/AccountBlocked';
import SuperadminDashboard from './pages/SuperadminDashboard';
import PublicPlans from './pages/PublicPlans';
import PublicDDS from './pages/PublicDDS';
import WhistleblowerPage from './pages/WhistleblowerPage';
import WhistleblowerAdmin from './pages/WhistleblowerAdmin';
import LandingPage from './pages/LandingPage';
// Employee-specific pages
import MeusTreinamentos from './pages/MeusTreinamentos';
import MinhasAvaliacoes from './pages/MinhasAvaliacoes';
import MeuPerfil from './pages/MeuPerfil';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { useParams, Navigate as RouterNavigate } from 'react-router-dom';

// Short link redirect component for activation
const ActivationRedirect = () => {
  const { token } = useParams();
  return <RouterNavigate to={`/ativar-conta?token=${token}`} replace />;
};

// Loading spinner for initial auth check
const AuthLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e8f4fc] to-white">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#0d9488] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#64748b] text-sm">Verificando sessão...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Wait for auth check to complete before deciding
  if (loading) {
    return <AuthLoading />;
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public only route - redirects to dashboard if already logged in
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <AuthLoading />;
  }
  
  // If user is logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Role-based route protection
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const isEmployee = user?.role === 'funcionario' || user?.role === 'employee';
  
  if (isEmployee) {
    return <AccessDenied requiredRole="administrador" />;
  }
  return children;
};

// Route for company Dashboard - ALWAYS shows data for the active company/CNPJ
// Superadmins access Global Panel via /superadmin/dashboard menu item
const SmartDashboardRoute = () => {
  const { user, loading } = useAuth();
  
  // Wait for auth to load
  if (loading) {
    return <AuthLoading />;
  }
  
  // Employees go to Employee Dashboard
  const isEmployee = user?.role === 'funcionario' || user?.role === 'employee';
  if (isEmployee) {
    return <EmployeeDashboard />;
  }
  
  // ALL other users (admin, gestor, superadmin) see the company Dashboard
  // This shows data filtered by the active company/CNPJ selected in CompanySwitcher
  // Superadmins access the Global Panel via the separate /superadmin/dashboard route
  return <Dashboard />;
};

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Landing Page - Public (redirects to dashboard if logged in) */}
            <Route path="/" element={<PublicOnlyRoute><LandingPage /></PublicOnlyRoute>} />
            
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/redefinir-senha/:token" element={<ResetPassword />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
            {/* Account activation - short link redirect */}
            <Route path="/a/:token" element={<ActivationRedirect />} />
            {/* Account activation - full path */}
            <Route path="/ativar-conta" element={<AtivarConta />} />
            {/* Employee Portal - no admin auth required */}
            <Route path="/funcionario" element={<EmployeePortal />} />
            {/* Public routes - no auth required */}
            <Route path="/avaliar/:token" element={<PublicAssessment />} />
            <Route path="/r/:token" element={<PublicAssessment />} />
            <Route path="/responder-avaliacao/:token" element={<PublicAssessment />} />
            <Route path="/aceitar-convite/:token" element={<AcceptInvite />} />
            {/* Subscription callback routes */}
            <Route path="/assinatura/sucesso" element={<SubscriptionSuccess />} />
            <Route path="/assinatura/erro" element={<SubscriptionError />} />
            <Route path="/assinatura/pendente" element={<SubscriptionPending />} />
            <Route path="/conta-bloqueada" element={<AccountBlocked />} />
            <Route path="/planos" element={<PublicPlans />} />
            
            {/* Public DDS route - no login required */}
            <Route path="/dds/v/:token" element={<PublicDDS />} />
            
            {/* Public Whistleblower route - anonymous, no login */}
            <Route path="/denuncia/:companySlug" element={<WhistleblowerPage />} />
            
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      {/* Smart Dashboard - shows different content based on role */}
                      <Route path="/" element={<SmartDashboardRoute />} />
                      <Route path="/dashboard" element={<SmartDashboardRoute />} />
                      
                      {/* Employee-only routes */}
                      <Route path="/minhas-avaliacoes" element={<MinhasAvaliacoes />} />
                      <Route path="/meu-perfil" element={<MeuPerfil />} />
                      
                      {/* Admin-only routes - protected */}
                      <Route path="/alerts" element={<AdminRoute><Alerts /></AdminRoute>} />
                      <Route path="/employees" element={<AdminRoute><Employees /></AdminRoute>} />
                      <Route path="/risks" element={<AdminRoute><Risks /></AdminRoute>} />
                      <Route path="/pgr" element={<AdminRoute><PGR /></AdminRoute>} />
                      <Route path="/aep" element={<AdminRoute><AEP /></AdminRoute>} />
                      <Route path="/reports" element={<AdminRoute><Reports /></AdminRoute>} />
                      <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
                      <Route path="/analise-ia" element={<AdminRoute><AIAnalysis /></AdminRoute>} />
                      <Route path="/turnover" element={<AdminRoute><Turnover /></AdminRoute>} />
                      <Route path="/offboarding" element={<AdminRoute><Offboarding /></AdminRoute>} />
                      <Route path="/absences" element={<AdminRoute><AbsencesReport /></AdminRoute>} />
                      <Route path="/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
                      <Route path="/assinatura" element={<AdminRoute><Subscription /></AdminRoute>} />
                      <Route path="/integracoes" element={<AdminRoute><Integrations /></AdminRoute>} />
                      <Route path="/esocial" element={<AdminRoute><ESocialConfig /></AdminRoute>} />
                      <Route path="/arquivos" element={<AdminRoute><Files /></AdminRoute>} />
                      <Route path="/empresa" element={<AdminRoute><CompanyProfile /></AdminRoute>} />
                      <Route path="/meu-contrato" element={<AdminRoute><MeuContrato /></AdminRoute>} />
                      <Route path="/master" element={<AdminRoute><MasterDashboard /></AdminRoute>} />
                      <Route path="/logs-notificacoes" element={<AdminRoute><NotificationLogs /></AdminRoute>} />
                      <Route path="/dds" element={<AdminRoute><DDSManagement /></AdminRoute>} />
                      <Route path="/denuncias" element={<AdminRoute><WhistleblowerAdmin /></AdminRoute>} />
                      <Route path="/trainings" element={<AdminRoute><Trainings /></AdminRoute>} />
                      
                      {/* Superadmin-only route */}
                      <Route path="/superadmin/dashboard" element={<SuperadminDashboard />} />
                      
                      {/* Shared routes - accessible by both admin and employees */}
                      <Route path="/assessments" element={<Assessments />} />
                      <Route path="/ranking" element={<Ranking />} />
                      <Route path="/action-plans" element={<ActionPlans />} />
                      <Route path="/ajuda" element={<HelpCenter />} />
                      <Route path="/meus-dds" element={<MeusDDS />} />
                      <Route path="/meus-treinamentos" element={<MeusTreinamentos />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <CookieConsent />
          <SupportButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
